import React, { useCallback, useState } from 'react';
import { View, ActivityIndicator } from 'react-native';
import { useInfiniteQuery, InfiniteData, useQueryClient } from '@tanstack/react-query';
import { FlashList } from '@shopify/flash-list';
import { Button, ButtonText } from '@/components/gluestack/button';
import { MessageView } from '@/components/ui/MessageView';
import { ApiError } from '@/services/ApiService';
import { useTranslation } from 'react-i18next';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { HeaderRefreshButton } from '@/components/ui/HeaderRefreshButton';
import { RefreshControl } from '@/components/gluestack/refresh-control';
import { SkeletonText } from '@/components/gluestack/skeleton';
import { createSkeletonItems } from '@/hooks/useInfiniteListUtils';
import { PageParam } from '@/hooks/usePaginationUtils';
import { Alert02Icon } from '@hugeicons-pro/core-stroke-rounded';

export type IconSvgElement = readonly (readonly [
  string,
  {
    readonly [key: string]: string | number;
  },
])[];

/**
 * Column configuration for DataList.
 * Defines the width, header, and content rendering for each column.
 */
export interface IColumnConfig<T> {
  /** Tailwind width class (e.g., 'w-32', 'flex-1') */
  width: string;
  /** Additional Tailwind classes for the column */
  className?: string;
  /** Render function for the header cell */
  renderHeader: () => React.ReactNode;
  /** Render function for the content cell */
  renderContent: (item: T) => React.ReactNode;
}

export interface IDataListProps<TData, TItem> {
  /** Screen title for header */
  title: string;
  /** React Query key for the query */
  queryKey: unknown[];
  /** Query function that returns paginated data */
  queryFn: (params: { pageParam: PageParam }) => Promise<TData>;
  /** Function to extract the next page param (skip or cursor) from the response */
  getNextPageParam: (lastPage: TData) => PageParam;
  /** Function to extract items array from each page */
  getPageItems: (page: TData) => TItem[];
  /** Column configurations */
  columns: IColumnConfig<TItem>[];
  /** Function to extract unique key from item */
  getItemKey: (item: TItem) => string;
  /** Empty state message */
  emptyMessage: string;
  /** Empty state icon */
  emptyIcon: IconSvgElement | undefined;
  /** Optional error message override */
  errorMessage?: string;
  /** Number of items to fetch per page */
  pageSize?: number;
  /** Content shown between the screen header and the list, including in empty and error states */
  headerContent?: React.ReactNode;
}

/**
 * Reusable component for rendering API-connected infinite scroll lists.
 * 
 * Features:
 * - Built-in screen header with refresh button (web) and pull-to-refresh (mobile)
 * - Infinite scroll pagination
 * - Column header row (not sticky: on web a sticky header spans the scrollbar and misaligns with the rows)
 * - Rows grouped in a white panel on the gray page background used by Search
 * - Loading, error, and empty states
 * - Automatic large screen padding
 * - Type-safe column configuration
 * 
 * @example
 * ```tsx
 * <DataList<TransactionHistoryView, TransactionHistoryItem>
 *   title={t('account.transactions.title')}
 *   queryKey={['billing', 'transactions']}
 *   queryFn={async ({ pageParam }) => await BillingService.getTransactionHistory(buildPaginationParams(pageParam, 25))}
 *   getNextPageParam={getNextPageSkipOrCursor}
 *   getPageItems={(page) => page.Items}
 *   getItemKey={(item) => String(item.Id)}
 *   emptyMessage={t('account.transactions.empty')}
 *   emptyIcon={EmptyIcon}
 *   columns={[
 *     {
 *       width: 'w-32',
 *       renderHeader: () => <Text>Date</Text>,
 *       renderContent: (item) => <Text>{dayjs(item.Date).format('YYYY-MM-DD')}</Text>
 *     },
 *     {
 *       width: 'flex-1',
 *       renderHeader: () => <Text>Description</Text>,
 *       renderContent: (item) => <Text>{item.Description}</Text>
 *     }
 *   ]}
 * />
 * ```
 */
export function DataList<TData, TItem extends object>({
  title,
  queryKey,
  queryFn,
  getNextPageParam,
  getPageItems,
  columns,
  getItemKey,
  emptyMessage,
  emptyIcon,
  errorMessage,
  pageSize = 25,
  headerContent,
}: IDataListProps<TData, TItem>) {
  const { t } = useTranslation();
  const isLargeScreen = useIsLargeScreen();
  const queryClient = useQueryClient();
  const [isUserRefreshing, setIsUserRefreshing] = useState(false);

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    isPending,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: undefined as PageParam,
    getNextPageParam,
    staleTime: 0,
    select: useCallback(
      (data: InfiniteData<TData, PageParam>) => {
        const items = data.pages.flatMap((page) => getPageItems(page));
        return { ...data, items } as InfiniteData<TData, PageParam> & {
          items: TItem[];
        };
      },
      [getPageItems]
    )
  });

  const items: TItem[] = (data as any)?.items ?? [];
  const skeletonItems = createSkeletonItems(10);
  const errorSubText = error
    ? error instanceof ApiError
      ? error.message
      : ((error as any)?.message ?? String(error))
    : undefined;

  const handleRefresh = useCallback(async () => {
    setIsUserRefreshing(true);

    queryClient.cancelQueries({ queryKey });
    await queryClient.resetQueries({ queryKey });

    setIsUserRefreshing(false);
  }, [queryClient, queryKey]);

  // Add header as first item
  const isLoading = isPending || isUserRefreshing;
  const listData = [
    { isHeader: true },
    ...(isLoading ? skeletonItems : items),
  ] as Array<{ isHeader: true } | TItem>;

  const pagePadding = isLargeScreen ? 'px-6' : 'px-4';

  // Rows form one white panel on the gray page: the header row opens it, the last row closes it
  const renderRow = (
    isLast: boolean,
    renderCell: (column: IColumnConfig<TItem>, index: number) => React.ReactNode
  ) => (
    <View className={pagePadding}>
      <View
        className={`mx-auto w-full max-w-4xl flex-row items-start border-x border-t border-outline-100 bg-background-0 px-4 py-3 ${isLast ? 'rounded-b-xl border-b' : ''}`}>
        {columns.map((column, index) => (
          <View key={index} className={`${column.width} ${column.className ?? ''} shrink-0`}>
            {renderCell(column, index)}
          </View>
        ))}
      </View>
    </View>
  );

  const renderItem = ({ item, index }: { item: { isHeader: true } | TItem; index: number }) => {
    const isLast = index === listData.length - 1;

    // Render header row
    if ('isHeader' in item && item.isHeader) {
      return (
        <View className={`pt-4 ${pagePadding}`}>
          <View className="mx-auto w-full max-w-4xl flex-row items-center rounded-t-xl border-x border-t border-outline-100 bg-background-0 px-4 py-3">
            {columns.map((column, index) => (
              <View key={index} className={`${column.width} ${column.className ?? ''} shrink-0 px-0`}>
                {column.renderHeader()}
              </View>
            ))}
          </View>
        </View>
      );
    }

    // Render skeleton row
    if (isLoading) {
      return renderRow(isLast, () => <SkeletonText className="h-5 w-5/6 rounded-md my-2" />);
    }

    // Render data row
    const dataItem = item as TItem;
    return renderRow(isLast, (column) => column.renderContent(dataItem));
  };

  return (
    <>
      <ScreenHeader
        options={{
          headerShown: true,
          title,
          headerRight: () => (
            <HeaderRefreshButton
              isLargeScreen={isLargeScreen}
              isRefreshing={isUserRefreshing}
              isDisabled={isRefetching}
              inAppHeaderBar={!isLargeScreen}
              onPress={handleRefresh}
            />
          ),
        }}
      />

      <View className="flex-1 bg-background-page">
        {headerContent && (
          <View className={`pt-4 ${pagePadding}`}>
            <View className="mx-auto w-full max-w-4xl">{headerContent}</View>
          </View>
        )}

        {error ? (
          <MessageView
            type="error"
            text={errorMessage ?? t('common.error.general')}
            subText={errorSubText}
            icon={Alert02Icon}>
            <Button variant="solid" action="primary" onPress={() => refetch()} disabled={isRefetching}>
              <ButtonText>{isRefetching ? t('common.loading') : t('common.retry')}</ButtonText>
            </Button>
          </MessageView>
        ) : !isLoading && items.length === 0 ? (
          <MessageView type="info" text={emptyMessage} icon={emptyIcon} />
        ) : (
          <FlashList
            data={listData}
            keyExtractor={(it, index) => 
              'isHeader' in it && it.isHeader 
                ? 'header' 
                : isLoading 
                  ? `skeleton-${index}` 
                  : getItemKey(it as TItem)
            }
            renderItem={renderItem}
            onEndReached={() => {
              if (!isLoading && hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
              }
            }}
            refreshControl={
              <RefreshControl refreshing={isUserRefreshing} onRefresh={handleRefresh} />
            }
            ListFooterComponent={
              isFetchingNextPage ? (
                <View className="px-4 py-3">
                  <ActivityIndicator color="grey" />
                </View>
              ) : (
                <View className="h-4" />
              )
            }
          />
        )}
      </View>
    </>
  );
}


